import {useMemo} from "react";
import {ArrowPathIcon, PauseCircleIcon, SparklesIcon} from "@heroicons/react/24/outline";
import NarrowDrawer from "../../../../../components/general/NarrowDrawer.tsx";
import {Tag} from "../../../../../components/general/Tag.tsx";
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import InvoicePreview, {InvoicePreviewSeller} from "../CreateInvoicePage/InvoicePreview.tsx";
import {computeInvoiceTotals} from "../CreateInvoicePage/utils/invoiceLines.ts";
import {RecurringSeriesDetails} from "./types/recurringInvoice.ts";
import {cadenceDescription, cadenceLabel, formatDateNL} from "./utils/recurringInvoiceFormat.ts";
import {nextInvoiceChanges, seriesLinesToPreviewLines} from "./utils/seriesToPreview.ts";

interface Props {
    open: boolean
    updateOpenState: (open: boolean) => void
    series: RecurringSeriesDetails | null
    seller: InvoicePreviewSeller
    buyerStreetWithHouseNumber: string
    buyerPostalCode: string
    buyerCity: string
    footerText: string
}

function ChangeIndicators({series}: { series: RecurringSeriesDetails }) {
    const changes = nextInvoiceChanges(series)

    return (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 shrink-0 text-primary"/>
                <p className="text-sm font-semibold text-gray-900">
                    Dit verandert in de volgende factuur
                </p>
            </div>
            <dl className="mt-3 space-y-2">
                {changes.map((change) => (
                    <div key={change.label} className="flex items-baseline justify-between gap-4 text-sm">
                        <dt className="shrink-0 text-gray-600">{change.label}</dt>
                        <dd className="text-right">
                            {change.value
                                ? <span className="font-semibold text-gray-900">{formatDateNL(change.value)}</span>
                                : <span className="italic text-gray-500">nog niet bekend</span>}
                            <p className="text-xs text-gray-500">{change.reason}</p>
                        </dd>
                    </div>
                ))}
            </dl>
            <p className="mt-3 border-t border-blue-200 pt-2 text-xs text-gray-600">
                Klant, regels, bedragen en betaaltermijn blijven ongewijzigd.
            </p>
        </div>
    )
}

export function RecurringSeriesPreviewDrawer({
    open,
    updateOpenState,
    series,
    seller,
    buyerStreetWithHouseNumber,
    buyerPostalCode,
    buyerCity,
    footerText,
}: Props) {
    const previewLines = useMemo(
        () => series ? seriesLinesToPreviewLines(series) : [],
        [series],
    )
    const totals = useMemo(() => computeInvoiceTotals(previewLines), [previewLines])

    return (
        <NarrowDrawer title="Volgende factuur" open={open} updateOpenState={updateOpenState}>
            {series && (
                <div className="flex h-full flex-col">
                    <div className="flex-1 space-y-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-base font-semibold text-gray-900">{series.customer_name}</p>
                                <p className="mt-0.5 text-sm text-gray-600">{series.description}</p>
                            </div>
                            <Tag variant="primary" size="xs">{cadenceLabel(series.cadence)}</Tag>
                        </div>

                        <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 text-sm">
                            <ArrowPathIcon className="h-5 w-5 shrink-0 text-gray-400"/>
                            <p className="text-gray-700">
                                Er wordt {cadenceDescription(series.cadence)} automatisch
                                een factuur klaargezet
                                {series.auto_send
                                    ? ' en direct naar de klant verstuurd.'
                                    : ' als concept; je verstuurt hem zelf.'}
                            </p>
                        </div>

                        {series.is_paused ? (
                            <div className="flex items-start gap-3 rounded-lg bg-yellow-50 p-4">
                                <PauseCircleIcon className="h-5 w-5 shrink-0 text-yellow-600"/>
                                <p className="text-sm text-yellow-800">
                                    Deze reeks is gepauzeerd, dus er wordt nu geen factuur aangemaakt.
                                    Hieronder zie je wat er klaargezet wordt zodra je de reeks weer activeert.
                                </p>
                            </div>
                        ) : (
                            <ChangeIndicators series={series}/>
                        )}

                        <div className="rounded-xl bg-gray-100 p-4">
                            <InvoicePreview
                                seller={seller}
                                buyerName={series.customer_name}
                                buyerStreetWithHouseNumber={buyerStreetWithHouseNumber}
                                buyerPostalCode={buyerPostalCode}
                                buyerCity={buyerCity}
                                invoiceNumber=""
                                invoiceDateISO={series.next_run_on}
                                paymentTermDays={series.payment_term}
                                purchaseOrderReference=""
                                customerSpecificReference={series.description}
                                lines={previewLines}
                                totals={totals}
                                footerText={footerText}
                            />
                        </div>
                    </div>

                    <div className="mt-8 flex items-center justify-end border-t border-gray-200 pt-4">
                        <SecondaryButton title="Sluiten" onClick={() => updateOpenState(false)}/>
                    </div>
                </div>
            )}
        </NarrowDrawer>
    )
}
